import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CrudStyles } from '../shared/Crud';
import ConfirmModal from '../shared/ConfirmModal';
import MessageModal from '../shared/MessageModal';
import routeService, {
    getPointDeliveryDetails,
    getRouteStatusValue,
    isRouteInCreation,
    isRouteInProgress,
    mapRouteDeliveryToPlanningShape,
    ROUTE_STATUS,
} from '../../services/routeService';
import { getFriendlyErrorMessage } from '../../utils/errorHandler';
import { formatDateTimeBR } from '../../utils/dateTimeDisplay';
import { ROUTES } from '../../routes';
import RouteStatusBadge from './RouteStatusBadge';
import RoutePointStateBadge from './RoutePointStateBadge';
import { getRoutePointStateValue } from './routePointStateConfig';
import RouteEditor from './RouteEditor';
import {
    getAddressForPointAction,
    getInstructionsForAction,
    normalizeRoutePointAction,
    ROUTE_POINT_ACTION_OPTIONS,
} from './routePointAction';
import {
    PageWrap,
    Panel,
    PanelHeader,
    RouteStopsColumn,
    StopCard,
    StopOrderBadge,
    StopCardBody,
    StopPointActionTitle,
    StopPointAddress,
    StopPointHeader,
    StopMeta,
    StopDescriptionHint,
    ConnectorLine,
    StopMotionWrap,
    MapPanelBody,
    MapEmpty,
    DetailHeaderMeta,
    DetailHeaderActions,
    DetailHeaderActionsGroup,
    DetailErrorBanner,
} from './RotasPage.styles';

const { Container, Header, Button } = CrudStyles;

function getPointActionLabel(action) {
    const value = normalizeRoutePointAction(
        typeof action === 'object' ? action?.value : action,
    );
    const opt = ROUTE_POINT_ACTION_OPTIONS.find((option) => option.value === value);
    return opt?.label || value || '—';
}

export default function RouteDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [route, setRoute] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [reloadTick, setReloadTick] = useState(0);
    const [messageModal, setMessageModal] = useState({ open: false, title: '', message: '' });
    const [confirmModal, setConfirmModal] = useState({
        open: false,
        title: '',
        message: '',
        confirmLabel: 'Confirmar',
        confirmVariant: 'primary',
        onConfirm: null,
    });

    useEffect(() => {
        let cancelled = false;

        (async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await routeService.get(id);
                if (!cancelled) {
                    setRoute(data);
                }
            } catch (err) {
                if (!cancelled) {
                    setRoute(null);
                    setError(getFriendlyErrorMessage(err) || 'Não foi possível carregar o roteiro.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [id, reloadTick]);

    const handleRouteChange = useCallback((update) => {
        if (!update || typeof update !== 'object') return;
        setRoute((prev) => (prev ? { ...prev, ...update } : prev));
    }, []);

    const routeState = getRouteStatusValue(route?.state);
    const points = useMemo(() => {
        const raw = Array.isArray(route?.points) ? [...route.points] : [];
        return raw.sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
    }, [route?.points]);

    const isEditable = isRouteInCreation(route?.state);
    const canPlan = isEditable;
    const canCancel =
        routeState &&
        routeState !== ROUTE_STATUS.CANCELLED &&
        routeState !== ROUTE_STATUS.FINISHED;
    const hasPendingPoints = useMemo(
        () => points.some((point) => getRoutePointStateValue(point.state) === 'PENDING'),
        [points],
    );
    const canShowFinish = Boolean(route) && isRouteInProgress(route?.state);
    const canFinish = canShowFinish && points.length > 0 && !hasPendingPoints;

    const handlePlan = async () => {
        setActionLoading(true);
        try {
            await routeService.plan(id);
            setReloadTick((value) => value + 1);
        } catch (err) {
            setMessageModal({
                open: true,
                title: 'Erro ao planejar',
                message: getFriendlyErrorMessage(err),
            });
        } finally {
            setActionLoading(false);
        }
    };

    const handleCancel = () => {
        setConfirmModal({
            open: true,
            title: 'Cancelar roteiro',
            message: 'Deseja cancelar este roteiro? Esta ação não pode ser desfeita.',
            confirmLabel: 'Cancelar roteiro',
            confirmVariant: 'danger',
            onConfirm: async () => {
                setConfirmModal((m) => ({ ...m, open: false }));
                setActionLoading(true);
                try {
                    await routeService.cancel(id);
                    setReloadTick((value) => value + 1);
                } catch (err) {
                    setMessageModal({
                        open: true,
                        title: 'Erro ao cancelar',
                        message: getFriendlyErrorMessage(err),
                    });
                } finally {
                    setActionLoading(false);
                }
            },
        });
    };

    const handleFinish = () => {
        if (!canFinish) return;
        setConfirmModal({
            open: true,
            title: 'Finalizar roteiro',
            message: 'Deseja finalizar este roteiro? Só confirme se todos os pontos já foram concluídos.',
            confirmLabel: 'Finalizar roteiro',
            confirmVariant: 'primary',
            onConfirm: async () => {
                setConfirmModal((m) => ({ ...m, open: false }));
                setActionLoading(true);
                try {
                    await routeService.finish(id);
                    setReloadTick((value) => value + 1);
                } catch (err) {
                    setMessageModal({
                        open: true,
                        title: 'Erro ao finalizar',
                        message: getFriendlyErrorMessage(err),
                    });
                } finally {
                    setActionLoading(false);
                }
            },
        });
    };

    const title = route?.alias || `Roteiro #${id}`;

    return (
        <PageWrap>
            <Container>
                <Header>
                    <div style={{ minWidth: 0 }}>
                        <h1>{loading ? 'Detalhes da rota' : title}</h1>
                        {!loading && route ? (
                            <DetailHeaderMeta>
                                <RouteStatusBadge state={route.state} />
                                <span>Motorista: {route.driverName || '—'}</span>
                                <span>Criado em: {formatDateTimeBR(route.creationDateTime)}</span>
                                {route.lastUpdateDateTime ? (
                                    <span>Atualizado em: {formatDateTimeBR(route.lastUpdateDateTime)}</span>
                                ) : null}
                            </DetailHeaderMeta>
                        ) : null}
                    </div>
                    <DetailHeaderActions>
                        <DetailHeaderActionsGroup>
                            {canPlan ? (
                                <Button
                                    type="button"
                                    variant="primary"
                                    onClick={handlePlan}
                                    disabled={actionLoading || loading}
                                >
                                    Planejar roteiro
                                </Button>
                            ) : null}
                            {canShowFinish ? (
                                <Button
                                    type="button"
                                    variant="primary"
                                    onClick={handleFinish}
                                    disabled={actionLoading || loading || !canFinish}
                                    title={
                                        hasPendingPoints
                                            ? 'Finalize somente quando nenhum ponto estiver pendente.'
                                            : undefined
                                    }
                                >
                                    Finalizar roteiro
                                </Button>
                            ) : null}
                        </DetailHeaderActionsGroup>
                        <DetailHeaderActionsGroup>
                            {canCancel ? (
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleCancel}
                                    disabled={actionLoading || loading}
                                >
                                    Cancelar roteiro
                                </Button>
                            ) : null}
                            <Button type="button" variant="outline" onClick={() => navigate(ROUTES.ROTAS)}>
                                Voltar à listagem
                            </Button>
                        </DetailHeaderActionsGroup>
                    </DetailHeaderActions>
                </Header>

                {loading ? (
                    <p style={{ margin: 0, color: '#6b7280' }}>Carregando roteiro…</p>
                ) : null}

                {error ? (
                    <DetailErrorBanner role="alert">
                        <p style={{ color: '#b91c1c', margin: 0, fontSize: 14 }}>{error}</p>
                        <Button type="button" variant="outline" onClick={() => setReloadTick((value) => value + 1)}>
                            Tentar novamente
                        </Button>
                    </DetailErrorBanner>
                ) : null}

                {!loading && !error && route && isEditable ? (
                    <RouteEditor
                        mode="edit"
                        routeId={id}
                        routeData={route}
                        onRouteChange={handleRouteChange}
                    />
                ) : null}

                {!loading && !error && route && !isEditable ? (
                    <Panel>
                        <PanelHeader>
                            Pontos do roteiro ({points.length})
                        </PanelHeader>
                        <MapPanelBody>
                            {points.length === 0 ? (
                                <MapEmpty>Nenhum ponto neste roteiro.</MapEmpty>
                            ) : (
                                <RouteStopsColumn>
                                    {points.map((point, index) => {
                                        const deliveryDetails = getPointDeliveryDetails(point);
                                        const deliveryShape =
                                            mapRouteDeliveryToPlanningShape(deliveryDetails);
                                        const action = normalizeRoutePointAction(point.targetAction?.value);
                                        const { address, title: addressTitle } = deliveryShape
                                            ? getAddressForPointAction(action, deliveryShape)
                                            : { address: '—', title: 'Endereço' };
                                        const instructions = getInstructionsForAction(action, deliveryShape);
                                        const desc = (point.description ?? '').trim();
                                        const driverComment = (point.driverComment ?? '').trim();
                                        const itemCount = deliveryShape?.items?.length ?? 0;

                                        return (
                                            <StopMotionWrap key={point.id}>
                                                <StopCard>
                                                    <StopOrderBadge aria-hidden>
                                                        {point.index ?? index + 1}
                                                    </StopOrderBadge>
                                                    <StopCardBody>
                                                        <StopPointHeader>
                                                            <StopPointActionTitle>
                                                                {getPointActionLabel(point.targetAction)}
                                                            </StopPointActionTitle>
                                                            <RoutePointStateBadge state={point.state} compact />
                                                        </StopPointHeader>
                                                        <StopPointAddress title={`${addressTitle}: ${address}`}>
                                                            {address}
                                                        </StopPointAddress>
                                                        {deliveryDetails?.id != null ? (
                                                            <StopMeta>
                                                                Entrega #{deliveryDetails.id}
                                                            </StopMeta>
                                                        ) : null}
                                                        {instructions ? (
                                                            <StopMeta title={instructions}>
                                                                Instruções: {instructions}
                                                            </StopMeta>
                                                        ) : null}
                                                        {itemCount > 0 ? (
                                                            <StopMeta>
                                                                Itens: {itemCount}
                                                            </StopMeta>
                                                        ) : null}
                                                        {desc ? (
                                                            <StopDescriptionHint title={desc}>
                                                                Observação: {desc}
                                                            </StopDescriptionHint>
                                                        ) : null}
                                                        {driverComment ? (
                                                            <StopDescriptionHint title={driverComment}>
                                                                Comentário do motorista: {driverComment}
                                                            </StopDescriptionHint>
                                                        ) : null}
                                                    </StopCardBody>
                                                </StopCard>
                                                {index < points.length - 1 ? <ConnectorLine aria-hidden /> : null}
                                            </StopMotionWrap>
                                        );
                                    })}
                                </RouteStopsColumn>
                            )}
                        </MapPanelBody>
                    </Panel>
                ) : null}

                {messageModal.open ? (
                    <MessageModal
                        title={messageModal.title}
                        message={messageModal.message}
                        onClose={() => setMessageModal((modal) => ({ ...modal, open: false }))}
                    />
                ) : null}

                <ConfirmModal
                    open={confirmModal.open}
                    title={confirmModal.title}
                    message={confirmModal.message}
                    confirmLabel={confirmModal.confirmLabel}
                    confirmVariant={confirmModal.confirmVariant}
                    onConfirm={confirmModal.onConfirm}
                    onCancel={() => setConfirmModal((m) => ({ ...m, open: false }))}
                />
            </Container>
        </PageWrap>
    );
}
